import { Component, Input } from '@angular/core';
import { EChartsOption } from 'echarts';
import { ScreenType } from 'src/app/enums/ScreenType';
import { VisitorStatusTimeline } from 'src/app/models/visitor';

@Component({
  selector: 'app-visitor-timeline-graph',
  templateUrl: './visitor-timeline-graph.component.html',
  styleUrl: './visitor-timeline-graph.component.scss',
})
export class VisitorTimelineGraphComponent {
  @Input() set chartHeight(val: any) {
    if (val) this.height = val + 'px';
  }
  @Input() set chartData(val: VisitorStatusTimeline[]) {
    if (val) {
      this.charData = val;
      if (this.isSet) {
        this.setChartData(this.charData);
      }
    }
  }
  @Input() set screenSize(val: ScreenType) {
    if (val) {
      this.screen = val;
      this.isSet = true;
      this.setChartData(this.charData);
    }
  }
  isSet = false;
  chartOption: EChartsOption;
  height = '300px';
  screen: ScreenType;
  charData: VisitorStatusTimeline[];
  ScreenType = ScreenType;

  // Calculate the chart width based on number of data points
  getChartWidth(): string {
    if (!this.charData || this.charData.length <= 5) {
      return '100%'; // Default width if data is small
    }

    // Calculate a width that ensures each data point has enough space
    // Minimum width of 100px per data point
    const minWidth = Math.max(this.charData.length * 100, 800);
    return `${minWidth}px`;
  }

  setChartData(val: VisitorStatusTimeline[]) {
    val = val.map((x) => {
      const temp = new Date(x.date)
        .toLocaleString('en-US', { hour12: false })
        .split(' ')[1]
        .toString()
        .split(':');
      const formatedData = parseFloat(temp[0] + '.' + temp[1]);
      return {
        ...x,
        time: formatedData,
      };
    });
    // Map of status to colors
    const statusColors: any = {
      '-1': '#FF0000', // Rejected - Red
      '0': '#FFA500', // Unverified - Orange
      '1': '#008000', // Verified - Green
      '3': '#FF4500', // AutoRejected - OrangeRed
      '4': '#1E90FF', // InProgress - DodgerBlue
      '5': '#32CD32', // Complete - LimeGreen
      '6': '#4682B4', // CheckedIn - SteelBlue
      '7': '#00008B', // CheckedOut - DarkBlue
      '8': '#A52A2A', // Created - Brown
      '9': '#808080', // Deleted - Gray
      '10': '#D3D3D3', // Unknown - LightGray
      '11': '#FFBF00', // ManuallyVerified - LightGray
      '12': '#8A2BE2', // ReScheduled - BlueViolet,
      '13': '#FF6347', // ManuallyCheckedOut - Tomato
    };
    const statusArray = val.map((item) => item.status);
    const xArray: string[] = val.map((item) =>
      new Date(item.date).toISOString()
    );
    const yArray = val.map((item) => item.time);

    // Prepare series data
    const seriesData = val.map((item) => ({
      dateTime: new Date(item.date).toLocaleString(),
      time: item.time,
      status: item.status,
    }));

    // Set chart options
    this.chartOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          return `DateTime: ${new Date(xArray[index]).toLocaleString('en-GB', {
            hour12: false,
          })}<br/> Status: ${this.getStatusData(
            statusArray[index],
            index,
            xArray[index]
          )}`;
        },
      },
      grid: {
        containLabel: true,
        left: '3%',
        right: '3%',
      },
      xAxis: {
        type: 'category',
        name: 'Date',
        data: xArray,
        axisLabel: {
          formatter: (value: string) => value.split('T')[0],
          rotate: this.screen == ScreenType.Mobile ? 45 : 0,
          interval: 0, // Show all labels
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 24,
        name: 'Time',
      },
      series: [
        {
          type: 'line',
          data: yArray,
          emphasis: {
            focus: 'series',
          },
          lineStyle: {
            width: 2, // Adjust the line width if needed
            color: '#000', // Use a neutral color for the line
          },
          itemStyle: {
            color: (params: any) => {
              const index = params.dataIndex;
              let colordata = statusColors[statusArray[index].toString()];
              return colordata;
            },
          },
          markPoint: {
            data: seriesData.map((item, index) => ({
              name: this.getStatusData(statusArray[index]),
              coord: [xArray[index], yArray[index]],
              value: this.getStatusData(statusArray[index]),
              itemStyle: {
                color: statusColors[statusArray[index].toString()],
              },
            })),
          },
        },
      ],
    };
  }

  getStatusData(val: number, val1?: any, val2?: any) {
    const statusMap: any = {
      '-1': 'Rejected',
      '0': 'Unverified',
      '1': 'Verified',
      '3': 'AutoRejected',
      '4': 'InProgress',
      '5': 'Complete',
      '6': 'CheckedIn',
      '7': 'CheckedOut',
      '8': 'Created',
      '9': 'Deleted',
      '10': 'Unknown',
      '11': 'ManuallyVerified',
      '12': 'ManuallyCheckedOut',
      '13': 'ReScheduled',
    };
    return statusMap[val.toString()] || 'Unknown';
  }
}
